<template>
	<AppSwitcherTheme />
	<div class="layout-authenticated">
		<TheNavigationMenu
			v-model="isNavigationMenuActive"
			class="no-print"
		/>
		<h1
			v-if="!showAppBar && !mobile"
			class="d-sr-only"
		>
			{{ pageGroupTitleState.title }}
		</h1>
		<VAppBar
			v-if="showAppBar || mobile"
			:class="[
				'layout-authenticated__app-bar no-print transition-none',
				!mobile && 'layout-authenticated__app-bar--desktop',
			]"
			color="axle-navbar-top"
			:height="headerHeight"
			flat
		>
			<div
				:class="[
					'layout-authenticated__app-bar-container bg-background w-100 px-4 pb-md-4 px-md-6',
					!mobile && 'mt-2 layout-authenticated__app-bar-container--rounded',
				]"
			>
				<CommonBreadcrumbs
					v-if="!mobile && isBreadcrumbsVisible"
					:items="breadcrumbItems"
				/>
				<div
					class="w-100 d-flex align-center align-self-stretch"
				>
					<VBtn
						v-if="mobile"
						class="mr-2"
						icon
						:aria-label="t('navigationMenu.expandMenu')"
						@click="isNavigationMenuActive = !isNavigationMenuActive"
					>
						<VIcon icon="menu" />
					</VBtn>
					<h1
						v-if="!!pageGroupTitleState.title"
						class="text-h1 text-truncate"
						data-testid="elementPageTitle"
					>
						{{ pageGroupTitleState.title }}
					</h1>
					<VSpacer />
					<VBtnSecondary
						v-if="!!userStore.impersonatedByProfile && !mobile"
						color="warning"
						data-testid="buttonLogoutImpersonation"
						:border="'sm solid warning opacity-100'"
						prepend-icon="co_present"
						@click="logOutImpersonation"
					>
						{{ t('returnTo', {name: impersonatedByName }) }}
					</VBtnSecondary>
					<VBtn
						v-if="!!userStore.impersonatedByProfile && mobile"
						color="warning"
						data-testid="buttonLogoutImpersonationMobile"
						icon
						:aria-label="t('logOut')"
						@click="logOutImpersonation"
					>
						<VIcon icon="co_present" />
					</VBtn>
					<CommonContextMenu
						ref="actionsMenu"
						activator-id="quickActionsMenu"
						:items="quickActionsMenuItems"
					>
						<template #activator>
							<VBtnPrimary
								id="quickActionsMenu"
								class="ml-2"
								prepend-icon="add"
								data-testid="buttonQuickActionsMenu"
							>
								{{ t('new') }}
							</VBtnPrimary>
						</template>
					</CommonContextMenu>
					<div
						v-if="pageGroupTitleState.items.length"
						class="d-flex align-center ga-1"
					>
						<VBtnTileSecondary
							v-for="item,index in pageGroupTitleState.items"
							:key="`${item.label} ${index}`"
							:data-testid="item.dataId"
							:aria-label="item.label"
							:color="item.iconColor"
							class="ml-2"
							@click="item.action"
						>
							<VIcon :icon="item.icon" />
						</VBtnTileSecondary>
					</div>
				</div>
			</div>
		</VAppBar>
		<VMain
			id="main-content"
			tabindex="-1"
			class="layout-authenticated__main transition-none"
		>
			<slot :key="profileSwitchKey" />
		</VMain>
		<CrudGalleryDrawer
			v-if="isCrudGalleryDrawerRendered"
			v-model="crudGalleryDrawerState.isOpen"
			:gallery="crudGalleryDrawerState.selectedGallery"
			@updated-gallery="crudGalleryDrawerState.onUpdateSuccess"
			@deleted-gallery="crudGalleryDrawerState.onDeleteSuccess"
		/>
		<CrudContentTaskDrawer
			v-if="isCrudContentTaskDrawerRendered"
			v-model="crudContentTaskDrawerState.isOpen"
			@created="markContentTaskAsCreated"
		/>
		<ContentHubLogoCrudDialog
			v-if="isLogoCrudDialogRendered"
			v-model="logoCrudDialogState.isOpen"
			:logo="logoCrudDialogState.activeLogo"
			:is-editing="logoCrudDialogState.isEditing"
			@mutated="markLogoAsMutated"
		/>
		<RosterUserAddModal
			v-if="isRosterUserAddModalRendered"
			v-model="isRosterUserAddModalOpen"
		/>
		<BulletinCrudModal
			v-if="isBulletinCrudModalRendered"
			v-model="bulletinState.isOpen"
			:is-editing="bulletinState.isEditing"
			:bulletin="bulletinState.activeBulletin"
			:selected-athletes="bulletinState.selectedAthletes"
			@updated-bulletin="bulletinState.onBulletinUpdated"
		/>
		<ExchangeUserListCreateDrawer
			v-if="isExchangeUserListCreateDrawerRendered"
			v-model="exchangeUserListState.isOpen"
			:selected-users="exchangeUserListState.selectedUsers"
		/>
		<AgreementDialog
			v-if="isAgreementDialogRendered"
			v-model="agreementDialogState.isOpen"
			v-model:rate-card="agreementDialogState.activeRateCard"
			:bulletin-uuid="agreementDialogState.activeBulletinUuid"
			:selected-athletes="agreementDialogState.selectedAthletes"
			:agreement-uuid="agreementDialogState.agreementUuid"
			:is-duplicating="agreementDialogState.isDuplicating"
			:is-editing="agreementDialogState.isEditing"
			@created="agreementDialogState.onCreated"
			@updated="agreementDialogState.onUpdated"
		/>
		<TransactionProposeDialog
			v-if="isTransactionProposeDialogRendered"
			v-model="transactionState.isOpen"
			v-model:rate-card="transactionState.activeRateCard"
			:bulletin-uuid="transactionState.activeBulletinUuid"
			:is-editing="transactionState.isEditing"
			:selected-athletes="transactionState.selectedAthletesUuids"
			:transaction-uuid="transactionState.activeTransactionUuid"
			@success="transactionState.onSuccess"
			@updated="transactionState.onUpdated"
		/>
		<TransactionDrawer
			v-if="isTransactionDrawerRendered"
			:key="transactionDrawerState.activeTransactionUuid || 'empty'"
			v-model="transactionDrawerState.isOpen"
			:transaction-uuid="transactionDrawerState.activeTransactionUuid"
			:reviews-remaining="transactionDrawerState.reviewsRemaining"
			@agreement-archived="transactionDrawerState.onAgreementArchived"
			@agreement-status-updated="transactionDrawerState.onAgreementStatusUpdated"
			@agreement-updated="transactionDrawerState.onAgreementUpdated"
			@archived="transactionDrawerState.onArchived"
			@payment-sent="transactionDrawerState.onPaymentSent"
			@preview="transactionDrawerState.onPreviewMedia"
			@reviewed="transactionDrawerState.onReviewed"
			@status-updated="transactionDrawerState.onUpdateStatusSuccess"
			@task-updated="transactionDrawerState.onTaskUpdated"
			@transaction-status-updated="transactionDrawerState.onUpdateTransactionStatusSuccess"
			@nil-go-status-updated="transactionDrawerState.onUpdateNilGoStatusSuccess"
			@updated="transactionDrawerState.onUpdated"
		/>
		<AgreementDrawer
			v-if="isAgreementDrawerRendered"
			:key="agreementDrawerState.activeAgreementUuid || 'empty'"
			v-model="agreementDrawerState.isOpen"
			:agreement-uuid="agreementDrawerState.activeAgreementUuid"
			@agreement-status-updated="agreementDrawerState.onUpdateStatusSuccess"
			@archived="agreementDrawerState.onArchived"
			@payment-sent="agreementDrawerState.onPaymentSent"
			@preview="agreementDrawerState.onPreviewMedia"
			@preview-contract="agreementDrawerState.onPreviewContract"
			@task-updated="agreementDrawerState.onTaskUpdated"
			@transaction-status-updated="agreementDrawerState.onTransactionStatusUpdated"
			@updated="agreementDrawerState.onUpdated"
		/>
		<TaskDrawer
			v-if="isTaskDrawerRendered"
			:key="taskDrawerState.activeTaskUuid || 'empty'"
			v-model="taskDrawerState.isOpen"
			:task-uuid="taskDrawerState.activeTaskUuid"
			:list-task="taskDrawerState.activeListTask"
			@updated="taskDrawerState.onUpdated"
		/>
		<BulletinDrawer
			v-if="isBulletinDrawerRendered"
			v-model="bulletinDrawerState.isOpen"
			:bulletin-uuid="bulletinDrawerState.activeBulletinUuid"
			@applied="bulletinDrawerState.onApplied"
			@preview="bulletinDrawerState.onPreviewMedia"
		/>
		<BulletinApplicantProfileDrawer
			v-if="isBulletinApplicantProfileDrawerRendered"
			v-model="bulletinApplicantProfileDrawerState.isOpen"
			:applicant="bulletinApplicantProfileDrawerState.applicant"
			:bulletin="bulletinApplicantProfileDrawerState.bulletin"
			:hide-actions="bulletinApplicantProfileDrawerState.hideActions"
			@send-message="bulletinApplicantProfileDrawerState.onSendMessage"
			@propose-transaction="bulletinApplicantProfileDrawerState.onProposeTransaction"
		/>
		<ExchangeAgreementPayeeDrawer
			v-if="isExchangeAgreementPayeeDrawerRendered"
			v-model="exchangeAgreementPayeeDrawerState.isOpen"
			:payee="exchangeAgreementPayeeDrawerState.payee"
			:hide-actions="exchangeAgreementPayeeDrawerState.hideActions"
			@updated="exchangeAgreementPayeeDrawerState.onUpdated"
			@update-all="exchangeAgreementPayeeDrawerState.onUpdateAll"
		/>
		<SendMessageDialog
			v-if="isSendMessageDialogRendered"
			v-model="sendMessageDialogState.isOpen"
			:selected-users="sendMessageDialogState.selectedUsers"
			@success="sendMessageDialogState.onSuccess"
			@error="sendMessageDialogState.onError"
		/>
		<ExchangeTransactionPaymentDialog
			v-if="isExchangeTransactionPaymentDialogRendered"
			v-model="exchangeTransactionPaymentDialogState.isOpen"
			v-model:transactions="exchangeTransactionPaymentDialogState.transactions"
			:cost-breakdown="exchangeTransactionPaymentDialogState.costBreakdown"
			:transactions-store-id="exchangeTransactionPaymentDialogState.transactionsStoreId"
			@payment-sent="exchangeTransactionPaymentDialogState.onPaymentSent"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, provide, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { whenever } from '@vueuse/core';
import { addToast } from '@/composables/useToasts';
import { useUserStore } from '@/stores/userStore';
import CommonBreadcrumbs from '@/components/CommonBreadcrumbs.vue';
import TheNavigationMenu from '@/components/TheNavigationMenu.vue';
import AppSwitcherTheme from '@/components/AppSwitcherTheme.vue';
import { CommonContextMenu, type ContextMenuItem } from '@teamworksdev/vuetify';
import { useBulletinDrawer } from '@/composables/useBulletinDrawer';
import { useAgreementDrawer } from '@/composables/useAgreementDrawer';
import { useBulletinApplicantProfileDrawer } from '@/composables/useBulletinApplicantProfileDrawer';
import { useBulletinCrudModal } from '@/composables/useBulletinCrudModal';
import { useCrudGalleryDrawer } from '@/composables/useCrudGalleryDrawer';
import { useCrudContentTaskDrawer } from '@/composables/useCrudContentTaskDrawer';
import { useContentHubLogoCrudDialog } from '@/composables/useContentHubLogoCrudDialog';
import { useImpersonationAuth } from '@/composables/useImpersonationAuth';
import { useUserLists } from '@/composables/useUserLists';
import { usePageGroupTitle } from '@/composables/usePageGroupTitle';
import { useTransactionProposeDialog } from '@/composables/useTransactionProposeDialog';
import { useExchangeTransactionPaymentDialog } from '@/composables/useExchangeTransactionPaymentDialog';
import { useExchangeAgreementPayeeDrawer } from '@/composables/useExchangeAgreementPayeeDrawer';
import { useSendMessageDialog } from '@/composables/useSendMessageDialog';
import { useTransactionDrawer } from '@/composables/useTransactionDrawer';
import { useTaskDrawer } from '@/composables/useTaskDrawer';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import { useAgreementDialog } from '@/composables/useAgreementDialog';
import { resolveVisibilityByProfileAccess } from '@/utils/helpers';
import { usePermissions } from '@/composables/usePermissions';
import { useLDFlag } from 'launchdarkly-vue-client-sdk';
import { FEATURE_FLAGS, PERMISSIONS, ROLES } from '@teamworksdev/influencer/core';

const BulletinCrudModal = defineAsyncComponent(() => import('@/components/exchange/BulletinCrudModal.vue'));
const CrudGalleryDrawer = defineAsyncComponent(() => import('@/components/content-hub/CrudGalleryDrawer.vue'));
const CrudContentTaskDrawer = defineAsyncComponent(() => import('@/components/content-tasks/CrudContentTaskDrawer.vue'));
const ContentHubLogoCrudDialog = defineAsyncComponent(() => import('@/components/content-hub/ContentHubLogoCrudDialog.vue'));
const AgreementDialog = defineAsyncComponent(() => import('@/components/AgreementDialog.vue'));
const TransactionProposeDialog = defineAsyncComponent(() => import('@/components/TransactionProposeDialog.vue'));
const TransactionDrawer = defineAsyncComponent(() => import('@/components/TransactionDrawer.vue'));
const AgreementDrawer = defineAsyncComponent(() => import('@/components/AgreementDrawer.vue'));
const BulletinApplicantProfileDrawer = defineAsyncComponent(() => import('@/components/exchange/BulletinApplicantProfileDrawer.vue'));
const ExchangeTransactionPaymentDialog = defineAsyncComponent(() => import('@/components/exchange/ExchangeTransactionPaymentDialog.vue'));
const BulletinDrawer = defineAsyncComponent(() => import('@/components/exchange/BulletinDrawer.vue'));
const ExchangeAgreementPayeeDrawer = defineAsyncComponent(() => import('@/components/exchange/ExchangeAgreementPayeeDrawer.vue'));
const TaskDrawer = defineAsyncComponent(() => import('@/components/exchange/TaskDrawer.vue'));
const RosterUserAddModal = defineAsyncComponent(() => import('@/components/roster/RosterUserAddModal.vue'));

const props = defineProps<{
	options?: Record<string, unknown>;
}>();

const showAppBar = computed<boolean>(() => {
	if (typeof props.options?.hideAppBar === 'boolean') return !props.options.hideAppBar;
	return true;
});

const { t } = useI18n();
const { mobile } = useDisplay();
const userStore = useUserStore();
const route = useRoute();
const { logOutImpersonation } = useImpersonationAuth();

const { hasReportTransactionsAccess, hasCreateContentTaskAccess, hasTeamPermission } = usePermissions();
const isContentTasksEnabled = useLDFlag(FEATURE_FLAGS.CONTENT_TASKS, false);
const isManageLogosEnabled = useLDFlag(FEATURE_FLAGS.MANAGE_LOGOS_ENABLED, false);
const isEditRosterNoHubAccessEnabled = useLDFlag(FEATURE_FLAGS.EDIT_ROSTER_NO_HUB_ACCESS, false);

// ROSTER USER ADD MODAL
const isRosterUserAddModalOpen = ref(false);
const isRosterUserAddModalRendered = ref(false);

function openRosterUserAddModal() {
	isRosterUserAddModalRendered.value = true;
	isRosterUserAddModalOpen.value = true;
}

const isNavigationMenuActive = ref(false);

const profileSwitchKey = ref(crypto.randomUUID());
watch(() => userStore.inflcrId, async () => {
	if ((await userStore.profileCanAccessCurrentRoute()) && !route.meta.profileSwitchRedirect) {
		profileSwitchKey.value = crypto.randomUUID();
	}
});

// PAGE GROUP TITLE
const { state: pageGroupTitleState } = usePageGroupTitle();

// CRUD GALLERY DRAWER
const {
	state: crudGalleryDrawerState,
	open: openCrudGalleryDrawer,
	isRendered: isCrudGalleryDrawerRendered,
} = useCrudGalleryDrawer();

// CRUD CONTENT TASK DRAWER
const {
	state: crudContentTaskDrawerState,
	open: openCrudContentTaskDrawer,
	markAsCreated: markContentTaskAsCreated,
	isRendered: isCrudContentTaskDrawerRendered,
} = useCrudContentTaskDrawer();

const {
	state: logoCrudDialogState,
	open: openCreateLogoDialog,
	markAsMutated: markLogoAsMutated,
	isRendered: isLogoCrudDialogRendered,
} = useContentHubLogoCrudDialog();

const {
	state: bulletinState,
	isRendered: isBulletinCrudModalRendered,
	open: openBulletinCrudModal,
} = useBulletinCrudModal();

const {
	breadcrumbItems,
	isBreadcrumbsVisible,
} = useBreadcrumbs();

const headerHeight = computed(() => {
	if (!mobile.value && isBreadcrumbsVisible.value) return 117;
	else if (route.name === 'NotFound' || route.name === 'NoAccess') return 77;
	else if (!mobile.value) return 81;
	return 60;
});

provide('headerHeight', headerHeight);

// EXCHANGE USER LIST CREATE DRAWER
const {
	state: exchangeUserListState,
	isRendered: isExchangeUserListCreateDrawerRendered,
	ExchangeUserListCreateDrawer,
} = useUserLists();

// AGREEMENT DIALOG
const {
	state: agreementDialogState,
	isRendered: isAgreementDialogRendered,
} = useAgreementDialog();

// TRANSACTION DIALOG
const {
	state: transactionState,
	isRendered: isTransactionProposeDialogRendered,
	open: openTransactionProposeDialog,
} = useTransactionProposeDialog({
	selectedAthletes: computed(() => {
		if (userStore.profile?.role_id !== ROLES.AGENT) return;
		return [userStore.profile.data.athlete.uuid];
	}),
});

// TRANSACTION DRAWER
const {
	state: transactionDrawerState,
	isRendered: isTransactionDrawerRendered,
} = useTransactionDrawer();

// AGREEMENT DRAWER
const {
	state: agreementDrawerState,
	isRendered: isAgreementDrawerRendered,
} = useAgreementDrawer();

// TASK DRAWER
const {
	state: taskDrawerState,
	isRendered: isTaskDrawerRendered,
} = useTaskDrawer();

// BULLETIN DRAWER
const {
	state: bulletinDrawerState,
	isRendered: isBulletinDrawerRendered,
} = useBulletinDrawer();

// BULLETIN APPLICANT PROFILE DRAWER
const {
	state: bulletinApplicantProfileDrawerState,
	isRendered: isBulletinApplicantProfileDrawerRendered,
} = useBulletinApplicantProfileDrawer();

// EXCHANGE AGREEMENT PAYEE DRAWER
const {
	state: exchangeAgreementPayeeDrawerState,
	isRendered: isExchangeAgreementPayeeDrawerRendered,
} = useExchangeAgreementPayeeDrawer();

// SEND MESSAGE DIALOG
const {
	state: sendMessageDialogState,
	isRendered: isSendMessageDialogRendered,
	SendMessageDialog,
} = useSendMessageDialog();

// PAYMENT DIALOG
const {
	state: exchangeTransactionPaymentDialogState,
	isRendered: isExchangeTransactionPaymentDialogRendered,
} = useExchangeTransactionPaymentDialog();

const impersonatedByName = computed(() => {
	const lastInitial = userStore.impersonatedByUser?.last_name?.charAt(0);
	return `${userStore.impersonatedByUser?.first_name} ${lastInitial ? lastInitial + '.' : ''}`;
});
const quickActionsMenuItems = computed<ContextMenuItem[]>(() => {
	const items: ContextMenuItem[] = [
		{
			icon: 'image',
			label: t('newGallery'),
			action: () => openCrudGalleryDrawer(),
			visible: computed(() => resolveVisibilityByProfileAccess('hasContentHubWrite')),
		},
		{
			icon: 'branding_watermark',
			label: t('newLogo'),
			action: () => openCreateLogoDialog(),
			visible: computed(() => isManageLogosEnabled.value && resolveVisibilityByProfileAccess('hasContentHubWrite')),
		},
		{
			icon: 'task',
			label: t('newContentTask'),
			action: () => openCrudContentTaskDrawer(),
			visible: computed(() => hasCreateContentTaskAccess.value && isContentTasksEnabled.value),
		},
		{
			icon: 'local_atm',
			label: t('reportTransaction'),
			action: () => openTransactionProposeDialog(),
			visible: computed(() => !userStore.profileAccess.hasLeaderboardRead && hasReportTransactionsAccess.value),
		},
		{
			icon: 'campaign',
			label: t('newBulletin'),
			action: () => openBulletinCrudModal(),
			visible: computed(() => userStore.brandCanCreateBulletins && ['ExchangeBulletins', 'ExchangeBulletin', 'ExchangeBulletinDetails', 'ExchangeBulletinApplicants'].some((name) => route.name === name) && resolveVisibilityByProfileAccess('canAccessBulletins')),
		},
		{
			icon: 'person_add',
			label: t('newUser'),
			action: () => openRosterUserAddModal(),
			visible: computed(() => isEditRosterNoHubAccessEnabled.value && hasTeamPermission(PERMISSIONS.TEAM_ADMIN).value),
		},
	];

	return items;
});

const { receivedConversationEvent } = storeToRefs(userStore);
whenever(receivedConversationEvent, (event) => {
	if (event.conversationUuid === route.params.conversationUuid) return;
	addToast({
		type: 'info',
		title: t('toast.messageReceived.title', { name: event.senderName }),
		description: event.message.text,
		link: {
			name: 'ExchangeConversation',
			params: { conversationUuid: event.conversationUuid },
		},
	});
});
</script>

<style scoped lang="scss">
.layout-authenticated {
	height: 100%;
	&__app-bar{
		transition: background-color 0.2s;
	}
	&__app-bar-container {
		padding-top: 17px;

		&--rounded {
			border-top-left-radius: 16px;
		}
	}
	&__main {
		height: 100%;
	}
}
</style>
